/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */

import { AxiosError } from "axios";
import { useEffect, useMemo, useState } from "react";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import { ADDRESS_API_KEY, ADDRESS_API_URL } from "@/constants";
import axioInstance from "@/libs/axios";
import { AddressType, QuoteCountryType } from "@/types/AddressTypes";
import { Nationality } from "@/types/QuestionPayloadType";
import { QuoteData } from "@/types/quote/quote-data";
import { trimFormValue } from "@/utils/form-input.utils";
import { sendTargetCountryToAddressService } from "@/utils/organiser.utils";

export const useAddressApi = (
  searchQuery: string | null,
  targetCountryValue: string | null | undefined
) => {
  const [addresses, setAddresses] = useState<AddressType[]>([]);
  const { current } = useWizard();

  const defaultFromCountry = useMemo(() => {
    return typeof current?.brandSpecificParams?.defaultFromCountry === "string"
      ? current.brandSpecificParams.defaultFromCountry
      : "GB";
  }, [current]);

  const fetchAddresses = async () => {
    try {
      const requestBody = {
        id: "12233444455",
        type: "addressLookup",
        attributes: {
          search: trimFormValue(searchQuery),
          disablePostCodeFilter: true,
        },
      };

      if (
        sendTargetCountryToAddressService ||
        current?.brandSpecificParams?.fromCountryEnabled
      ) {
        Object.assign(requestBody.attributes, {
          ...(sendTargetCountryToAddressService && {
            targetCountry: targetCountryValue,
          }),
          ...(current?.brandSpecificParams?.fromCountryEnabled && {
            targetCountry: defaultFromCountry,
          }),
        });
      }

      const config = {
        headers: {
          "x-api-key": `${ADDRESS_API_KEY}`,
        },
        withCredentials: false,
      };
      const response = await axioInstance.post(
        `${ADDRESS_API_URL}/address/find`,
        requestBody,
        config
      );
      setAddresses(response.data.data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching addresses:", error);
      setAddresses([]);
    }
  };

  return [addresses, fetchAddresses] as [AddressType[], () => void];
};

export const useSearchCountry = (searchQuery: string | null) => {
  const [addresses, setAddresses] = useState<QuoteCountryType[]>([]);

  const [loading, setLoading] = useState<boolean>(true);

  const fetchAddresses = async () => {
    try {
      const requestBody = {
        type: "countryLookup",
        id: "12345",
        attributes: {
          search: trimFormValue(searchQuery),
          filter: "*",
          limit: 20,
        },
      };

      const config = {
        headers: {
          "x-api-key": `${ADDRESS_API_KEY}`,
        },
        withCredentials: false,
      };
      setLoading(true);
      const response = await axioInstance.post(
        `${ADDRESS_API_URL}/country/find`,
        requestBody,
        config
      );
      setAddresses(response.data.data);
      setLoading(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching country data:", error);
      setAddresses([]);
      setLoading(false);
    }
  };

  return [addresses, loading, fetchAddresses] as [
    QuoteCountryType[],
    boolean,
    () => void,
  ];
};

export const useFetchAddressData = (searchQuery: string) => {
  const [addresse, setAddresse] = useState<AddressType[]>([]);

  const fetchAddresses = async () => {
    try {
      const requestBody = {
        id: "randomUUID",
        type: "addressLookup",
        attributes: {
          search: searchQuery,
          disablePostCodeFilter: true,
        },
      };
      const config = {
        headers: {
          "x-api-key": `${ADDRESS_API_KEY}`,
        },
        withCredentials: false,
        data: requestBody,
      };
      const response = await axioInstance.get(
        `${ADDRESS_API_URL}/address/${searchQuery}`,
        config
      );
      setAddresse(response.data.data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching address data:", error);
      setAddresse([]);
    }
  };

  return [addresse, fetchAddresses] as [AddressType[], () => void];
};

// for organiser and traveller nationality list
export const useFetchNationalityData = (isFetchForExpat: boolean) => {
  const [countries, setCountries] = useState<Nationality[]>([]);

  // current date
  const currentDate = new Date();
  const formattedDate = currentDate.toISOString().split("T")[0];

  useEffect(() => {
    if (!isFetchForExpat) return;

    const fetchCountries = async () => {
      try {
        const response = await axioInstance.get(
          `/referenceData/${formattedDate}`
        );
        setCountries(response.data.data.attributes.nationality);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching nationality data:", error);
        setCountries([]);
      }
    };

    fetchCountries();
  }, [isFetchForExpat]);

  return countries;
};

export default function useSaveAddressDetails(
  payloadData: AddressType,
  quote: QuoteData
) {
  const [response, setResponse] = useState<any>(null);

  const saveAddressDetails = async () => {
    if (payloadData && quote) {
      const payLoad: any = {
        data: {
          type: quote.type,
          channel: "ONLINE",
          version: quote?.version,
          attributes: {
            domain: quote?.attributes?.domain,
            organiser: {
              postCode: trimFormValue(payloadData.attributes.postalCode),
              firstLineOfAddress: trimFormValue(payloadData.attributes.line1),
              secondLineOfAddress: trimFormValue(
                payloadData.attributes?.line2 || ""
              ),
              city: trimFormValue(payloadData.attributes.city),
              country: payloadData.attributes.countryIso2,
            },
          },
        },
      };
      try {
        const res = await axioInstance.patch(
          `quote/${quote.referenceId}/organiser`,
          payLoad
        );
        setResponse(res.data.data);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error saving address details:", error);
      }
    }
  };

  return [response, saveAddressDetails];
}
